import React, { useMemo, useRef, useEffect, useCallback, useState } from "react";
import AssetPanel from "./AssetPanel";
import Lines from "./Lines";
import { useTheme } from "@mui/material/styles";
import Uns from "../uns/Uns";
import { DataTag } from "../../components/MediaFactory";
import { AgentNode } from "../../components/AgentNode";
import colorUtils from "../../Utils/ColorUtil";
import useMqttTopics from "../../hooks/useMqttTopics";
import useSimulatedData from "../../hooks/useSimulatedData";
import useSimulatedAgents from "../../hooks/useSimulatedAgents";
import { useApp } from "../../hooks/useApp";
import { useDocs } from "../../hooks/useDocs";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Typography from "@mui/material/Typography";

function detectMediaType(url) {
  if (!url) return "image";
  const lower = url.toLowerCase();
  if (
    lower.includes(".mp4") ||
    lower.includes(".webm") ||
    lower.includes(".mov") ||
    lower.includes(".mkv") ||
    lower.includes("youtube.com") ||
    lower.includes("youtu.be") ||
    lower.includes("wistia.com") ||
    lower.includes("vimeo.com")
  ) {
    return "video";
  }
  return "image";
}

function getYouTubeEmbedUrl(url) {
  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/
  );
  return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1&loop=1&playlist=${match[1]}` : null;
}

export default function FactoryAndPanel({
  setSelectedAsset,
  selectedAsset,
  selectedSuite,
  imgHeight,
  nodes,
  hasSidebar = false,
  preconnectedMqttValues = {},
  preconnectedMqttRecentlyChanged = new Set(),
  // Simulation props
  simulatedValues = null,
  simulatedRecentlyChanged = null,
  simulatedAgentStates = null,
  simulatedAgents = null,
  // MQTT config override from megafactory level
  megafactoryMqttConfig = null,
  // Hide the discover section (title and description)
  hideDiscover = false,
}) {
  const parentIsSimulation = simulatedValues !== null;
  const theme = useTheme();
  const { devMode } = useApp();
  const { updateDocument } = useDocs("vtec_assets");
  const factoryRef = useRef(null);
  const dragRef = useRef(null);
  const [posOverrides, setPosOverrides] = useState({});
  const posOverridesRef = useRef(posOverrides);
  posOverridesRef.current = posOverrides;
  const prevDevModeRef = useRef(devMode);
  const beaconDelaysRef = useRef({});
  const youarehere = selectedSuite
    ? selectedSuite.Children?.find((asset) => asset.youarehere)
    : [];

  const handleAsset = (selected) => {
    if (selectedAsset && selectedAsset.id === selected.id) {
      setSelectedAsset(null);
    } else {
      setSelectedAsset(selected);
    }
  };

  const isUns =
    selectedSuite &&
    ["tHktnIRtcSYdmjTa0CZe", "kuDiqPpyVcemLYIzLJF7", "acsYeEJmvcjQsrHPDjks", "XrCSd4K3fyKo1u4yNp8N", "XJ2C9UNdjT0arnEsRxT6"].includes(selectedSuite.id);

  const containerWidth = hasSidebar ? "calc(100vw - 420px)" : "calc(100vw - 40px)";

  const [imageScale, setImageScale] = useState(selectedSuite?.imageScale || 1);

  useEffect(() => {
    setImageScale(selectedSuite?.imageScale || 1);
    setPosOverrides({});
  }, [selectedSuite?.id, selectedSuite?.imageScale]);

  // Save position overrides to Firestore when dev mode is turned off
  useEffect(() => {
    const wasDevMode = prevDevModeRef.current;
    prevDevModeRef.current = devMode;

    if (wasDevMode && !devMode && selectedSuite?.id) {
      const overrides = posOverridesRef.current;
      const hasOverrides = Object.keys(overrides).length > 0;
      const updates = {};

      // Save imageScale if it changed
      if (imageScale !== (selectedSuite?.imageScale || 1)) {
        updates.imageScale = imageScale;
      }

      if (hasOverrides) {
        // Update Children positions (Left/Top are percentage strings like "45%")
        if (selectedSuite.Children?.length) {
          updates.Children = selectedSuite.Children.map((child) => {
            const ov = overrides[child.id];
            if (!ov) return child;
            const newLeft = parseFloat(child.Left) + ov.dx;
            const newTop = parseFloat(child.Top) + ov.dy;
            return { ...child, Left: `${newLeft}%`, Top: `${newTop}%` };
          });
        }

        // Update dataTags positions (position.x/y are numbers)
        if (selectedSuite.dataTags?.length) {
          updates.dataTags = selectedSuite.dataTags.map((tag) => {
            const ov = overrides[tag.id];
            if (!ov) return tag;
            return {
              ...tag,
              position: {
                ...tag.position,
                x: (tag.position?.x ?? 50) + ov.dx,
                y: (tag.position?.y ?? 50) + ov.dy,
              },
            };
          });
        }

        // Update agents positions (position.x/y are numbers)
        if (selectedSuite.agents?.length) {
          updates.agents = selectedSuite.agents.map((agent) => {
            const ov = overrides[agent.id];
            if (!ov) return agent;
            return {
              ...agent,
              position: {
                ...agent.position,
                x: (agent.position?.x ?? 50) + ov.dx,
                y: (agent.position?.y ?? 50) + ov.dy,
              },
            };
          });
        }
      }

      if (Object.keys(updates).length > 0) {
        updateDocument(selectedSuite.id, updates, "vtec_assets");
        setPosOverrides({});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devMode]);

  const agents = useMemo(() => selectedSuite?.agents || [], [selectedSuite?.agents]);
  const dataTags = useMemo(() => selectedSuite?.dataTags || [], [selectedSuite?.dataTags]);
  
  // Priority: megafactory override > suite-level mqttConfig > unsConfig.mqttConfig
  const unsConfigMqtt = selectedSuite?.unsConfig?.mqttConfig;
  const mqttConfig = (megafactoryMqttConfig?.broker ? megafactoryMqttConfig : null) || (selectedSuite?.mqttConfig?.broker ? selectedSuite.mqttConfig : null) || (unsConfigMqtt?.broker ? unsConfigMqtt : null);

  // Three-layer data source: parent simulation > per-node > group > factory > default "live"
  const factoryDataSource = selectedSuite?.dataSource || selectedSuite?.dataTagsSource || 'live';
  const tagGroupDataSource = selectedSuite?.dataTagGroupDataSource || factoryDataSource;
  const agentGroupDataSource = selectedSuite?.agentGroupDataSource || factoryDataSource;

  const resolveTagDataSource = useCallback((tag) => {
    if (parentIsSimulation) return 'simulated';
    return tag.dataSource || tagGroupDataSource;
  }, [parentIsSimulation, tagGroupDataSource]);

  const resolveAgentDataSource = useCallback((agent) => {
    if (parentIsSimulation) return 'simulated';
    return agent.dataSource || agentGroupDataSource;
  }, [parentIsSimulation, agentGroupDataSource]);

  // Partition tags and agents into live vs simulated
  const liveTags = useMemo(() => dataTags.filter((t) => resolveTagDataSource(t) === 'live'), [dataTags, resolveTagDataSource]);
  const simTags = useMemo(() => dataTags.filter((t) => resolveTagDataSource(t) === 'simulated'), [dataTags, resolveTagDataSource]);
  const simAgents = useMemo(() => agents.filter((a) => resolveAgentDataSource(a) === 'simulated'), [agents, resolveAgentDataSource]);

  const videoControlTopic = selectedSuite?.videoControlTopic || "";
  const videoStartValue = selectedSuite?.videoStartValue || "Running";
  const videoStopValue = selectedSuite?.videoStopValue || "Stopped";
  const videoRef = useRef(null);

  // Topic paths for live tags only
  const liveTagTopics = useMemo(() => {
    const topics = liveTags.map(t => t.topicPath || t.id).filter(Boolean);
    if (videoControlTopic) topics.push(videoControlTopic);
    return Array.from(new Set(topics));
  }, [liveTags, videoControlTopic]);

  // Topic paths for simulated tags
  const simTagTopics = useMemo(() => {
    return simTags.map(t => t.topicPath || t.id).filter(Boolean);
  }, [simTags]);

  // Live MQTT for live tags (no-op when no live tags or no config)
  const { values: liveTagValues, recentlyChanged: liveTagChanged } = useMqttTopics(
    liveTags.length > 0 && !parentIsSimulation ? mqttConfig : null,
    liveTags.length > 0 && !parentIsSimulation ? liveTagTopics : []
  );

  // Simulated data tags (only for simulated tags, not parent-driven)
  const { values: simTagValues, recentlyChanged: simTagChanged } = useSimulatedData(
    simTags.length > 0 && !parentIsSimulation ? simTagTopics : [],
    simTags.length > 0 && !parentIsSimulation ? simTags : []
  );

  // Simulated agents (only for simulated agents, not parent-driven)
  const { agentStates: internalSimAgentStates } = useSimulatedAgents(
    simAgents.length > 0 && !parentIsSimulation ? simAgents : []
  );

  // Merge live + simulated tag values (live takes priority)
  const tagValues = useMemo(() => {
    if (parentIsSimulation) return simulatedValues || {};
    return { ...simTagValues, ...liveTagValues };
  }, [parentIsSimulation, simulatedValues, simTagValues, liveTagValues]);

  const tagChanged = useMemo(() => {
    if (parentIsSimulation) return simulatedRecentlyChanged || new Set();
    return new Set([...liveTagChanged, ...simTagChanged]);
  }, [parentIsSimulation, simulatedRecentlyChanged, liveTagChanged, simTagChanged]);

  // Resolve final agent states — per-agent
  const effectiveAgentStates = parentIsSimulation
    ? simulatedAgentStates
    : internalSimAgentStates;

  useEffect(() => {
    if (!videoControlTopic || !videoStartValue || !videoStopValue) return;
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const rawValue = liveTagValues?.[videoControlTopic];
    if (rawValue === undefined || rawValue === null) return;

    const normalized = String(rawValue).trim().toLowerCase();
    if (normalized === videoStartValue.trim().toLowerCase()) {
      videoEl.play().catch(() => {});
    } else if (normalized === videoStopValue.trim().toLowerCase()) {
      videoEl.pause();
    }
  }, [liveTagValues, videoControlTopic, videoStartValue, videoStopValue]);

  // Cleanup drag listeners on unmount
  useEffect(() => {
    return () => {
      if (dragRef.current?.onMove) {
        window.removeEventListener("pointermove", dragRef.current.onMove);
        window.removeEventListener("pointerup", dragRef.current.onUp);
      }
    };
  }, []);

  // Dev mode dragging
  const handleDragStart = useCallback((e, id) => {
    if (!devMode) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = factoryRef.current?.getBoundingClientRect();
    if (!rect) return;
    const existing = posOverridesRef.current[id] || { dx: 0, dy: 0 };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const { id: dragId, startX, startY, rect: r, baseDx, baseDy } = dragRef.current;
      const dx = baseDx + ((ev.clientX - startX) / r.width) * 100;
      const dy = baseDy + ((ev.clientY - startY) / r.height) * 100;
      setPosOverrides((prev) => ({ ...prev, [dragId]: { dx, dy } }));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      dragRef.current = null;
    };
    dragRef.current = { id, startX: e.clientX, startY: e.clientY, rect, baseDx: existing.dx, baseDy: existing.dy, onMove, onUp };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [devMode]);

  const applyOverride = useCallback((id, baseX, baseY) => {
    const ov = posOverrides[id];
    if (!ov) return { x: baseX, y: baseY };
    return { x: baseX + ov.dx, y: baseY + ov.dy };
  }, [posOverrides]);

  const applyOverrideStr = useCallback((id, leftStr, topStr) => {
    const ov = posOverrides[id];
    if (!ov) return { left: leftStr, top: topStr };
    const baseX = parseFloat(leftStr);
    const baseY = parseFloat(topStr);
    return { left: `${baseX + ov.dx}%`, top: `${baseY + ov.dy}%` };
  }, [posOverrides]);

  return (
    <div
      id="main-container"
      style={{
        display: "flex",
        flexDirection: "row",
        width: containerWidth,
        height: "calc(100vh - 65px)",
        justifyContent: isUns ? "space-between" : "center",
        alignItems: isUns ? "flex-start" : "center",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {isUns ? (
        <Uns unsId={selectedSuite.id} hideTree={hasSidebar} />
      ) : (
        <div
          id="factory"
          ref={factoryRef}
          style={{
            backgroundColor: "transparent",
            position: "relative",
            height: "fit-content",
            maxWidth: hideDiscover ? (hasSidebar ? "70vw" : "85vw") : (hasSidebar ? "55vw" : "70vw"),
            zIndex: 1,
          }}
        >
          {nodes && <Lines nodes={nodes} />}

          {/* Beacon Layer */}
          <div
            id="beaconlayer"
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
            }}
          >
            {selectedSuite &&
              selectedSuite.Children?.map((asset) => {
                const beaconColor =
                  selectedAsset && selectedAsset.id === asset.id
                    ? asset.color || theme.palette.primary.main
                    : asset.color || selectedSuite.color;

                // Auto decide text color
                const fontColor = colorUtils.isColorDark(beaconColor)
                  ? "#FFFFFF"
                  : "#000000";
        
                if (beaconDelaysRef.current[asset.id] === undefined) {
                  beaconDelaysRef.current[asset.id] = Math.random();
                }
                const beaconPos = applyOverrideStr(asset.id, asset.Left, asset.Top);
                return (
                  asset.Title && (
                    <div
                      key={asset.id}
                      className="appbeacon-wrapper"
                      style={{
                        top: beaconPos.top,
                        left: beaconPos.left,
                        zIndex:
                          selectedAsset && selectedAsset.id === asset.id
                            ? 5
                            : 4,
                        "--beaconcolor": (selectedAsset && selectedAsset.id === asset.id) ? fontColor : beaconColor,
                        "--fontcolor": (selectedAsset && selectedAsset.id === asset.id) ? beaconColor  : fontColor,
                        "--delay": `${beaconDelaysRef.current[asset.id]}s`,
                        position: "absolute",
                        cursor: devMode ? "grab" : undefined,
                      }}
                      onPointerDown={devMode ? (e) => handleDragStart(e, asset.id) : undefined}
                    >
                      <div
                        className={
                          selectedAsset && selectedAsset.id === asset.id
                            ? "appbeacon"
                            : "appbeacon vibey"
                        }
                        id={asset.id}
                        onClick={() => handleAsset(asset)}
                      >
                        <svg viewBox="0 0 14.4004 11.1992" fill="currentColor" width="16" height="16" style={{ flexShrink: 0 }}>
                          <path d="M13.1641 0C13.8439 0.000126492 14.4002 0.553911 14.4004 1.24512V9.96582C14.4004 10.6509 13.844 11.1991 13.1641 11.1992H1.23633C0.556433 11.1991 0 10.6509 0 9.96582V1.24512C0.000173734 0.560135 0.556539 0.00012681 1.23633 0H13.1641ZM1.23633 9.97266H13.1641V1.2334H1.23633V9.97266ZM12 8.7998H10.4004V8H12V8.7998ZM6.94336 6.29297V8.33301L4.7998 6.27832V4.23828L6.94336 6.29297ZM9.60059 6.29297L7.47266 8.33301V6.29297L9.60059 4.25V6.29297ZM8.71484 4.37891L7.20605 5.82227L5.70117 4.37891L7.20605 2.93262L8.71484 4.37891ZM4.7998 3.19922H2.40039V2.39941H4.7998V3.19922Z" />
                        </svg>
                        {asset.Title}
                        {asset?.AiFeatures?.length ? (
                          <div
                            className="flex items-center justify-center rounded"
                            style={{
                              padding: '0 4px',
                              fontSize: 11,
                              fontWeight: 700,
                              backgroundColor: !(selectedAsset && selectedAsset.id === asset.id) ? fontColor : beaconColor,
                              color: !(selectedAsset && selectedAsset.id === asset.id) ? beaconColor : fontColor
                            }}
                          >
                            +AI
                          </div>
                        ) : null}
                      </div>
                      <div className="appbeacon-stem" />
                      <div
                        className="cappdot"
                        style={{ backgroundColor: beaconColor }}
                      />
                    </div>
                  )
                );
              })}


            {/* Selected asset connector */}
            {selectedSuite &&
              selectedSuite.Children?.map(
                (asset) => {
                  const connPos = applyOverrideStr(asset.id, asset.Left, asset.Top);
                  return selectedAsset &&
                  selectedAsset.id === asset.id &&
                  asset.Title && (
                    <svg
                      key={asset.id}
                      width="100%"
                      height="100%"
                      style={{ position: "absolute", zIndex: 2 }}
                    >
                      <line
                        x1={connPos.left}
                        y1={connPos.top}
                        x2={"calc(100% - 20px + 2.5px)"}
                        y2={connPos.top}
                        stroke={"#939EAB"}
                        strokeWidth={"5px"}
                      />
                      <line
                        x1={"calc(100% - 20px)"}
                        y1={connPos.top}
                        x2={"calc(100% - 20px)"}
                        y2={"50%"}
                        stroke={"#939EAB"}
                        strokeWidth={"5px"}
                      />
                      <line
                        x1={"calc(100% - 20px - 2.5px)"}
                        y1={"50%"}
                        x2={"100%"}
                        y2={"50%"}
                        stroke={"#939EAB"}
                        strokeWidth={"5px"}
                      />
                    </svg>
                  );
                }
              )}

            {/* You Are Here connector */}
            {!selectedAsset && youarehere && (() => {
              const yahPos = applyOverrideStr(youarehere.id, youarehere.Left, youarehere.Top);
              return (
                <svg
                  key={youarehere.id}
                  width="100%"
                  height="100%"
                  style={{ position: "absolute", zIndex: 2 }}
                >
                  <line
                    x1={yahPos.left}
                    y1={yahPos.top}
                    x2={"calc(100% - 20px + 2.5px)"}
                    y2={yahPos.top}
                    stroke={"#939EAB"}
                    strokeWidth={"5px"}
                  />
                  <line
                    x1={"calc(100% - 20px)"}
                    y1={yahPos.top}
                    x2={"calc(100% - 20px)"}
                    y2={"42%"}
                    stroke={"#939EAB"}
                    strokeWidth={"5px"}
                  />
                  <line
                    x1={"calc(100% - 20px - 2.5px)"}
                    y1={"42%"}
                    x2={"100%"}
                    y2={"42%"}
                    stroke={"#939EAB"}
                    strokeWidth={"5px"}
                  />
                </svg>
              );
            })()}

            {selectedSuite &&
              selectedSuite?.AiFeatures?.map((asset) => {
                const beaconColor =
                  selectedAsset && selectedAsset.id === asset.id
                    ? theme.palette.customColors.highlight
                    : asset.color || selectedSuite.color;

                // Auto decide text color
                const fontColor = colorUtils.isColorDark(beaconColor)
                  ? "#FFFFFF"
                  : "#000000";

                if (beaconDelaysRef.current[asset.id] === undefined) {
                  beaconDelaysRef.current[asset.id] = Math.random();
                }
                const aiPos = applyOverrideStr(asset.id, asset.Left, asset.Top);
                return (
                  <div
                    key={asset.id}
                    className="appbeacon-wrapper"
                    style={{
                      top: aiPos.top,
                      left: aiPos.left,
                      zIndex:
                        selectedAsset && selectedAsset.id === asset.id ? 5 : 4,
                      "--beaconcolor": beaconColor,
                      "--fontcolor": fontColor,
                      "--delay": `${beaconDelaysRef.current[asset.id]}s`,
                      position: "absolute",
                      cursor: devMode ? "grab" : undefined,
                    }}
                    onPointerDown={devMode ? (e) => handleDragStart(e, asset.id) : undefined}
                  >
                    <div
                      className={
                        selectedAsset && selectedAsset.id === asset.id
                          ? "appbeacon"
                          : "appbeacon vibey"
                      }
                      id={asset.id}
                      onClick={() => handleAsset(asset)}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style={{ opacity: 0.85, flexShrink: 0 }}>
                        <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
                      </svg>
                      {asset.Title}
                    </div>
                    <div className="appbeacon-stem" />
                    <div
                      className="cappdot"
                      style={{ backgroundColor: beaconColor }}
                    />
                  </div>
                );
              })}


            {/* Selected AI feature connector */}
            {selectedSuite &&
              selectedSuite?.AiFeatures?.map(
                (asset) => {
                  const aiConnPos = applyOverrideStr(asset.id, asset.Left, asset.Top);
                  return selectedAsset &&
                  selectedAsset.id === asset.id && (
                    <svg
                      key={asset.id}
                      width="100%"
                      height="100%"
                      style={{ position: "absolute", zIndex: 2 }}
                    >
                      <line
                        x1={aiConnPos.left}
                        y1={aiConnPos.top}
                        x2={"calc(100% - 20px + 2.5px)"}
                        y2={aiConnPos.top}
                        stroke={"#939EAB"}
                        strokeWidth={"5px"}
                      />
                      <line
                        x1={"calc(100% - 20px)"}
                        y1={aiConnPos.top}
                        x2={"calc(100% - 20px)"}
                        y2={"50%"}
                        stroke={"#939EAB"}
                        strokeWidth={"5px"}
                      />
                      <line
                        x1={"calc(100% - 20px - 2.5px)"}
                        y1={"50%"}
                        x2={"100%"}
                        y2={"50%"}
                        stroke={"#939EAB"}
                        strokeWidth={"5px"}
                      />
                    </svg>
                  );
                }
              )}

            {/* Data Tags */}
            {dataTags.map((tag) => {
              const valueKey = tag.topicPath || tag.id;
              const tagPos = applyOverride(tag.id, tag.position?.x ?? 50, tag.position?.y ?? 50);
              return (
                <DataTag
                  key={tag.id}
                  label={tag.label}
                  value={tagValues[valueKey]}
                  position={tagPos}
                  style={{ ...tag.style, ...(devMode ? { cursor: "grab" } : {}) }}
                  isHot={tagChanged.has(valueKey)}
                  onPointerDown={devMode ? (e) => handleDragStart(e, tag.id) : undefined}
                />
              );
            })}

            {/* Agent Nodes — per-agent data source resolution */}
            {agents.length > 0 && agents.map((agent) => {
              const agentIsSimulated = resolveAgentDataSource(agent) === 'simulated';
              const agentPos = applyOverride(agent.id, agent.position?.x ?? 50, agent.position?.y ?? 50);
              return agentIsSimulated ? (
                <AgentNode
                  key={agent.id}
                  name={agent.name}
                  topicBase={agent.topicBase}
                  position={agentPos}
                  style={agent.style}
                  icon={agent.icon}
                  simulatedState={effectiveAgentStates?.[agent.id]}
                  onPointerDown={devMode ? (e) => handleDragStart(e, agent.id) : undefined}
                  devMode={devMode}
                />
              ) : (
                mqttConfig && (
                  <AgentNode
                    key={agent.id}
                    name={agent.name}
                    topicBase={agent.topicBase}
                    position={agentPos}
                    mqttConfig={mqttConfig}
                    style={agent.style}
                    icon={agent.icon}
                    onPointerDown={devMode ? (e) => handleDragStart(e, agent.id) : undefined}
                    devMode={devMode}
                  />
                )
              );
            })}
          </div>

          {/* Background Media (auto-detects video vs image) */}
          {(() => {
            const mediaUrl = selectedSuite?.mediaUrl || selectedSuite?.img || selectedSuite?.Image || "https://tulip.widen.net/content/u79nz7brvd/webp/transparent-factory-floor-compressed-.webp";
            const mediaType = detectMediaType(mediaUrl);
            const mediaStyle = {
              maxWidth: "100%",
              maxHeight: `calc(100vh - ${imgHeight ? imgHeight : 180}px)`,
              opacity: 1,
              backgroundColor: "transparent",
              transform: `scale(${imageScale})`,
              transformOrigin: "center center",
              transition: "transform 0.2s ease",
            };

            if (mediaType === "video") {
              const ytEmbed = getYouTubeEmbedUrl(mediaUrl);
              if (ytEmbed) {
                return (
                  <iframe
                    src={ytEmbed}
                    title={selectedSuite?.Title || "Factory video"}
                    style={{ ...mediaStyle, width: "100%", height: `calc(100vh - ${imgHeight ? imgHeight : 180}px)`, border: "none" }}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />
                );
              }
              return (
                <video
                  ref={videoRef}
                  src={mediaUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{ ...mediaStyle, pointerEvents: "none" }}
                />
              );
            }

            return (
              <img
                src={mediaUrl}
                alt={selectedSuite?.Title ? `${selectedSuite.Title} layout` : "Factory layout"}
                style={mediaStyle}
              />
            );
          })()}
        </div>
      )}

      {/* Node type legend */}
      {selectedSuite?.showLegend !== false && (dataTags.length > 0 || agents.length > 0 || selectedSuite?.Children?.length > 0) && (
        <div
          style={{
            position: "absolute",
            bottom: 12,
            right: 12,
            display: "flex",
            alignItems: "center",
            gap: 16,
            backgroundColor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(42px)",
            borderRadius: 8,
            padding: "4px 12px",
            zIndex: 50,
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: 12,
            color: "#0f1c2c",
          }}
        >
          <span>Asset types:</span>
          {selectedSuite?.Children?.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{ width: 16, height: 16, borderRadius: 2.667, backgroundColor: selectedSuite.color || "#4E79A7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg viewBox="0 0 14.4004 11.1992" fill="white" width="14" height="14"><path d="M13.1641 0C13.8439 0.000126492 14.4002 0.553911 14.4004 1.24512V9.96582C14.4004 10.6509 13.844 11.1991 13.1641 11.1992H1.23633C0.556433 11.1991 0 10.6509 0 9.96582V1.24512C0.000173734 0.560135 0.556539 0.00012681 1.23633 0H13.1641ZM1.23633 9.97266H13.1641V1.2334H1.23633V9.97266ZM12 8.7998H10.4004V8H12V8.7998ZM6.94336 6.29297V8.33301L4.7998 6.27832V4.23828L6.94336 6.29297ZM9.60059 6.29297L7.47266 8.33301V6.29297L9.60059 4.25V6.29297ZM8.71484 4.37891L7.20605 5.82227L5.70117 4.37891L7.20605 2.93262L8.71484 4.37891ZM4.7998 3.19922H2.40039V2.39941H4.7998V3.19922Z" /></svg>
              </div>
              <span>Tulip Apps</span>
            </div>
          )}
          {agents.length > 0 && (
            <>
              {selectedSuite?.Children?.length > 0 && <div style={{ width: 1, height: 17, backgroundColor: "#ccc" }} />}
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <div style={{ width: 16, height: 16, borderRadius: 2.667, backgroundColor: agents[0]?.style?.color || "#B07AA1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 16 16" fill="none" width="14" height="14"><path d="M8 5.333V2.667H5.333M1.333 9.333h1.334M13.333 9.333h1.334M10 8.667V10M6 8.667V10M4 5.333h8a1.333 1.333 0 011.333 1.334V12A1.333 1.333 0 0112 13.333H4A1.333 1.333 0 012.667 12V6.667A1.333 1.333 0 014 5.333z" stroke="white" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <span>AI Agent</span>
              </div>
            </>
          )}
          {dataTags.length > 0 && (
            <>
              {(selectedSuite?.Children?.length > 0 || agents.length > 0) && <div style={{ width: 1, height: 17, backgroundColor: "#ccc" }} />}
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <div style={{ width: 16, height: 16, borderRadius: 2.667, backgroundColor: dataTags[0]?.style?.color || "#F28E2B", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 14 14" fill="white" width="14" height="14"><path d="M7 12.25c-1.468 0-2.71-.226-3.726-.678C2.258 11.12 1.75 10.568 1.75 9.917V4.083c0-.641.513-1.192 1.539-1.648C4.314 1.978 5.551 1.75 7 1.75s2.686.228 3.711.685c1.026.457 1.539 1.007 1.539 1.648v5.834c0 .651-.508 1.203-1.524 1.655-1.016.452-2.258.678-3.726.678zM7 5.265a9.81 9.81 0 002.61-.372c.875-.248 1.366-.513 1.473-.795-.107-.282-.596-.549-1.466-.802A9.635 9.635 0 007 2.917c-.885 0-1.752.124-2.603.372-.851.248-1.344.515-1.48.81.136.291.63.559 1.48.81A9.81 9.81 0 007 5.265zm0 2.902c.408 0 .802-.02 1.181-.058.38-.04.741-.095 1.087-.168a8.59 8.59 0 00.977-.27c.306-.107.586-.228.838-.365V5.556a8.59 8.59 0 01-.838.365 8.36 8.36 0 01-.977.27c-.346.073-.713.128-1.087.168A11.14 11.14 0 017 6.417c-.408 0-.807-.02-1.196-.058a10.1 10.1 0 01-1.1-.168 8.36 8.36 0 01-.97-.27 6.25 6.25 0 01-.817-.365v1.75c.243.136.523.258.817.365.301.107.624.197.97.27.345.073.712.128 1.1.168.39.039.784.058 1.196.058zm0 2.916c.447 0 .902-.034 1.364-.102.461-.068.887-.158 1.276-.27.389-.112.714-.238.977-.38.262-.14.418-.284.467-.43V8.473a8.588 8.588 0 01-.839.365 8.36 8.36 0 01-.977.27c-.345.072-.712.128-1.086.167A11.14 11.14 0 017 9.333c-.408 0-.807-.02-1.196-.058a10.1 10.1 0 01-1.1-.168 8.36 8.36 0 01-.97-.27 6.25 6.25 0 01-.817-.365v1.445c.049.146.202.287.46.423.257.136.58.26.97.372.388.112.816.202 1.283.27.467.068.924.102 1.37.102z" /></svg>
                </div>
                <span>Data</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Image Zoom Control — hidden until dev mode activated via 5x logo click */}
      {!isUns && devMode && (
        <div
          style={{
            position: "absolute",
            bottom: 16,
            left: 16,
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: 24,
            padding: "2px 4px",
            zIndex: 50,
            backdropFilter: "blur(8px)",
          }}
        >
          <IconButton
            size="small"
            onClick={() => setImageScale((s) => Math.max(0.5, +(s - 0.1).toFixed(1)))}
            sx={{ color: "#fff", padding: "4px" }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              color: "#fff",
              fontSize: "12px",
              minWidth: 40,
              textAlign: "center",
              userSelect: "none",
              cursor: "pointer",
            }}
            onClick={() => setImageScale(1)}
            title="Reset to 100%"
          >
            {Math.round(imageScale * 100)}%
          </Typography>
          <IconButton
            size="small"
            onClick={() => setImageScale((s) => Math.min(3, +(s + 0.1).toFixed(1)))}
            sx={{ color: "#fff", padding: "4px" }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      <AssetPanel
        selectedAsset={selectedAsset}
        setSelectedAsset={setSelectedAsset}
        discover={selectedSuite ? selectedSuite.discover : ""}
        discoverHeader={
          selectedSuite ? selectedSuite.discoverHeader ?? null : ""
        }
        youarehere={youarehere}
        hideDiscover={hideDiscover}
      />
    </div>
  );
}
